/**
 * pages/procurement/ContractsListPage.jsx — Contracts Register.
 *
 * Table of contracts with search/filter/CRUD/download + a left drawer for
 * create/edit. Row "View" navigates to the per-type detail page.
 */
import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Plus, Eye, Pencil, Trash2, FileText, FileSignature, AlertTriangle } from 'lucide-react';

import MainLayout from '../../components/MainLayout';
import { PageHeader } from '../../components/PageHeader';
import { DataTable } from '../../components/data-table';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from '../../components/ui/select';
import { ContractDrawer } from '../../components/procurement/ContractDrawer';
import {
  listContracts, deleteContract, getContract, getSummary, COMMODITIES, CONTRACT_TYPES,
} from '../../api/contracts';
import { getCurrentAccount } from '../../lib/auth';

const STATUS_VARIANT = {
  active: 'default', expired: 'destructive', upcoming: 'secondary', draft: 'outline',
};
const fmtDate = (d) => (d ? new Date(d).toLocaleDateString() : '—');
const labelOf = (list, v) => list.find((x) => x.value === v)?.label || v || '—';

const ALL = '__all__';

const Tile = ({ label, value, icon: Icon, accent }) => (
  <div className="border rounded-md p-4 bg-card" data-testid={`contracts-tile-${label.toLowerCase().replace(/\s+/g, '-')}`}>
    <div className="flex items-center justify-between">
      <span className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">{label}</span>
      {Icon && <Icon className={`w-4 h-4 ${accent || 'text-muted-foreground'}`} />}
    </div>
    <div className="text-3xl font-bold mt-2 tracking-tight">{value}</div>
  </div>
);

export default function ContractsListPage() {
  const navigate = useNavigate();
  const account = getCurrentAccount();
  const accountId = account?.id;

  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [offset, setOffset] = useState(0);
  const [loading, setLoading] = useState(true);
  const [summary, setSummary] = useState(null);
  const [filters, setFilters] = useState({ q: '', commodity: ALL, contract_type: ALL, status: ALL });
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [editing, setEditing] = useState(null);

  const limit = 25;

  const load = useCallback(async () => {
    if (!accountId) return;
    setLoading(true);
    try {
      const params = { accountId, limit, offset };
      if (filters.q) params.q = filters.q;
      if (filters.commodity !== ALL) params.commodity = filters.commodity;
      if (filters.contract_type !== ALL) params.contract_type = filters.contract_type;
      if (filters.status !== ALL) params.status = filters.status;
      const data = await listContracts(params);
      setRows(data.items);
      setTotal(data.total);
    } catch (e) {
      toast.error('Failed to load contracts', { description: e?.response?.data?.detail || e.message });
    } finally {
      setLoading(false);
    }
  }, [accountId, offset, filters]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    if (!accountId) return;
    getSummary({ accountId }).then(setSummary).catch(() => {});
  }, [accountId, total]);

  const openCreate = () => { setEditing(null); setDrawerOpen(true); };
  const openEdit = async (row) => {
    try {
      const full = await getContract({ accountId, id: row.id });
      setEditing(full);
      setDrawerOpen(true);
    } catch (e) {
      toast.error('Failed to open contract', { description: e?.response?.data?.detail || e.message });
    }
  };
  const handleDelete = async (row) => {
    if (!window.confirm(`Delete contract "${row.name}"? This cannot be undone.`)) return;
    try {
      await deleteContract({ accountId, id: row.id });
      toast.success('Contract deleted');
      load();
    } catch (e) {
      toast.error('Delete failed', { description: e?.response?.data?.detail || e.message });
    }
  };

  const columns = [
    { key: 'name', header: 'Contract', accessor: (r) => (
      <button onClick={() => navigate(`/procurement/contracts/${r.id}`)} className="font-medium hover:text-primary text-left" data-testid={`contract-link-${r.id}`}>
        {r.name}
      </button>
    ) },
    { key: 'vendor', header: 'Vendor', accessor: (r) => <span className="text-sm">{r.vendor_name || '—'}</span> },
    { key: 'commodity', header: 'Commodity', accessor: (r) => <span className="text-sm capitalize">{labelOf(COMMODITIES, r.commodity)}</span> },
    { key: 'type', header: 'Type', accessor: (r) => <Badge variant="outline">{labelOf(CONTRACT_TYPES, r.contract_type)}</Badge> },
    { key: 'start', header: 'Start', accessor: (r) => <span className="text-sm whitespace-nowrap">{fmtDate(r.start_date)}</span> },
    { key: 'end', header: 'End', accessor: (r) => <span className="text-sm whitespace-nowrap">{fmtDate(r.end_date)}</span> },
    { key: 'owner', header: 'Owner', accessor: (r) => <span className="text-sm">{r.contract_owner || '—'}</span> },
    { key: 'status', header: 'Status', accessor: (r) => (
      <div className="flex flex-col gap-0.5">
        <Badge variant={STATUS_VARIANT[r.status] || 'outline'} className="capitalize w-fit">{r.status}</Badge>
        {r.days_to_expiry !== null && r.status !== 'expired' && (
          <span className={`text-[11px] ${r.days_to_expiry <= 90 ? 'text-amber-600' : 'text-muted-foreground'}`}>
            {r.days_to_expiry} days to expiry
          </span>
        )}
      </div>
    ) },
    { key: 'docs', header: 'Docs', accessor: (r) => (
      <span className="text-xs text-muted-foreground flex items-center gap-1"><FileText className="w-3.5 h-3.5" />{r.document_count}</span>
    ) },
  ];

  return (
    <MainLayout>
      <div className="max-w-[1400px] mx-auto space-y-6">
        <PageHeader
          title="Contracts"
          description="Central repository of utility supply contracts — coverage, supplier exposure, performance and renewals."
          actions={
            <Button onClick={openCreate} data-testid="contracts-new-btn">
              <Plus className="w-4 h-4 mr-1.5" /> New Contract
            </Button>
          }
        />

        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <Tile label="Active Contracts" value={summary?.active ?? '—'} icon={FileSignature} accent="text-emerald-600" />
          <Tile label="Total Contracts" value={summary?.total ?? '—'} icon={FileText} />
          <Tile label="Vendors" value={summary?.vendors ?? '—'} icon={FileSignature} />
          <Tile label="Expiring ≤90d" value={summary?.expiring_90 ?? '—'} icon={AlertTriangle} accent="text-amber-600" />
        </div>

        <DataTable
          columns={columns}
          rows={rows}
          total={total}
          offset={offset}
          limit={limit}
          loading={loading}
          onPageChange={setOffset}
          exportFilename={`contracts-${accountId}.csv`}
          testId="contracts-table"
          emptyTitle="No contracts yet"
          emptyHint="Create your first utility supply contract to start tracking coverage and performance."
          emptyAction={<Button size="sm" onClick={openCreate} data-testid="contracts-empty-cta"><Plus className="w-4 h-4 mr-1.5" /> New Contract</Button>}
          extraToolbar={
            <div className="flex flex-wrap items-center gap-2">
              <Input
                value={filters.q}
                onChange={(e) => { setOffset(0); setFilters((f) => ({ ...f, q: e.target.value })); }}
                placeholder="Search by name…"
                className="w-52"
                data-testid="contracts-search"
              />
              <Select value={filters.commodity} onValueChange={(v) => { setOffset(0); setFilters((f) => ({ ...f, commodity: v })); }}>
                <SelectTrigger className="w-40" data-testid="contracts-filter-commodity"><SelectValue placeholder="Commodity" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value={ALL}>All commodities</SelectItem>
                  {COMMODITIES.map((c) => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}
                </SelectContent>
              </Select>
              <Select value={filters.contract_type} onValueChange={(v) => { setOffset(0); setFilters((f) => ({ ...f, contract_type: v })); }}>
                <SelectTrigger className="w-36" data-testid="contracts-filter-type"><SelectValue placeholder="Type" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value={ALL}>All types</SelectItem>
                  {CONTRACT_TYPES.map((c) => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}
                </SelectContent>
              </Select>
              <Select value={filters.status} onValueChange={(v) => { setOffset(0); setFilters((f) => ({ ...f, status: v })); }}>
                <SelectTrigger className="w-32" data-testid="contracts-filter-status"><SelectValue placeholder="Status" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value={ALL}>All status</SelectItem>
                  <SelectItem value="active">Active</SelectItem>
                  <SelectItem value="upcoming">Upcoming</SelectItem>
                  <SelectItem value="expired">Expired</SelectItem>
                  <SelectItem value="draft">Draft</SelectItem>
                </SelectContent>
              </Select>
            </div>
          }
          rowActions={(r) => (
            <div className="flex justify-end gap-0.5">
              <Button size="sm" variant="ghost" onClick={() => navigate(`/procurement/contracts/${r.id}`)} title="View" data-testid={`contract-view-${r.id}`}><Eye className="w-4 h-4" /></Button>
              <Button size="sm" variant="ghost" onClick={() => openEdit(r)} title="Edit" data-testid={`contract-edit-${r.id}`}><Pencil className="w-4 h-4" /></Button>
              <Button size="sm" variant="ghost" onClick={() => handleDelete(r)} title="Delete" data-testid={`contract-delete-${r.id}`}><Trash2 className="w-4 h-4 text-destructive" /></Button>
            </div>
          )}
        />
      </div>

      <ContractDrawer open={drawerOpen} onOpenChange={setDrawerOpen} accountId={accountId} existing={editing} onSaved={load} />
    </MainLayout>
  );
}
